import React, { useEffect, useState } from 'react';
import QRCode from 'qrcode';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import { Copy, Download } from 'lucide-react';
import { paths } from '../routes/paths';
import { Button } from './ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';

/** Public purchase URL for a raffle (board raffles use the square picker). */
export function rifaPublicUrl(rifa) {
  const path = rifa?.tipo === 'board' ? paths.buyBoard(rifa._id) : paths.buyTickets(rifa._id);
  return `${window.location.origin}${path}`;
}

/** Modal with a QR code pointing buyers to the raffle's public purchase page. */
export default function RifaQrDialog({ rifa, open, onOpenChange }) {
  const { t } = useTranslation();
  const [dataUrl, setDataUrl] = useState('');
  const url = rifa ? rifaPublicUrl(rifa) : '';

  useEffect(() => {
    if (!open || !url) return;
    let cancelled = false;
    QRCode.toDataURL(url, { width: 1024, margin: 2, errorCorrectionLevel: 'M' })
      .then(png => !cancelled && setDataUrl(png))
      .catch(() => toast.error(t('raffle.qrError', 'No se pudo generar el código QR')));
    return () => {
      cancelled = true;
    };
  }, [open, url, t]);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(url);
      toast.success(t('raffle.qrLinkCopied', 'Enlace copiado'));
    } catch {
      toast.error(url);
    }
  };

  const handleDownload = () => {
    const slug = String(rifa?.nombre || 'bono')
      .toLowerCase()
      .normalize('NFD')
      .replace(/[̀-ͯ]/g, '')
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-|-$/g, '');
    const a = document.createElement('a');
    a.href = dataUrl;
    a.download = `qr-${slug || rifa?._id}.png`;
    a.click();
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className='max-w-sm'>
        <DialogHeader>
          <DialogTitle>{t('raffle.qrTitle', 'Código QR del bono')}</DialogTitle>
          <DialogDescription>{rifa?.nombre}</DialogDescription>
        </DialogHeader>
        <div className='flex flex-col items-center gap-3'>
          {dataUrl ? (
            <img
              src={dataUrl}
              alt={t('raffle.qrTitle', 'Código QR del bono')}
              className='h-64 w-64 rounded-md border border-border bg-white'
            />
          ) : (
            <div className='h-64 w-64 animate-pulse rounded-md bg-muted' />
          )}
          <a
            href={url}
            target='_blank'
            rel='noreferrer'
            className='break-all text-center text-xs text-muted-foreground underline'
          >
            {url}
          </a>
        </div>
        <DialogFooter className='gap-2'>
          <Button variant='outline' onClick={handleCopy}>
            <Copy className='h-4 w-4' />
            {t('raffle.qrCopyLink', 'Copiar enlace')}
          </Button>
          <Button disabled={!dataUrl} onClick={handleDownload}>
            <Download className='h-4 w-4' />
            {t('raffle.qrDownload', 'Descargar PNG')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
